import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Form, Button, Table, Alert, Spinner,
    Modal, InputGroup, Badge
} from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency } from "../utils/formatters";
// NE PAS IMPORTER TVA_TAUX, CSS_TAUX
// import { TVA_TAUX, CSS_TAUX } from "../utils/constantes";
import './Factures.css';

export default function ModifierDevis() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [clients, setClients] = useState([]);
    const [produits, setProduits] = useState([]);
    const [showProduitModal, setShowProduitModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [originalStatut, setOriginalStatut] = useState('');
    
    // ÉTATS POUR LES TAXES
    const [taxes, setTaxes] = useState({
        tva_taux: 18,
        css_taux: 1,
        tva_active: true,
        css_active: true
    });

    // État du formulaire
    const [devis, setDevis] = useState({
        id_client: '',
        date_proforma: '',
        date_validite: '',
        remise_globale: 0,
        notes: '',
        statut: 'brouillon',
        lignes: []
    });

    useEffect(() => {
        fetchDevis();
        fetchClients();
        fetchProduits();
        fetchTaxes(); // AJOUTER
    }, [id]);

    // FONCTION POUR CHARGER LES TAXES
    const fetchTaxes = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/parametres.php?action=taxes',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) {
                    setTaxes(result.data);
                }
            }
        } catch (err) {
            console.error("Erreur chargement taxes", err);
        }
    };

    const fetchDevis = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/devis.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setDevis(result.data);
            setOriginalStatut(result.data.statut); // Sauvegarder le statut original
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchClients = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/clients.php',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setClients(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement clients", err);
        }
    };

    const fetchProduits = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/produits.php?include_stock=true',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                // S'assurer que stock_actuel est défini
                const produitsAvecStock = (result.data || []).map(p => ({
                    ...p,
                    stock_actuel: p.stock_actuel || 0,
                    seuil_alerte: p.seuil_alerte || 5
                }));
                setProduits(produitsAvecStock);
            }
        } catch (err) {
            console.error("Erreur chargement produits", err);
        }
    };

    const ajouterLigne = (produit) => {
        const nouvelleLigne = {
            id_produit: produit.id_produit,
            reference: produit.reference,
            designation: produit.designation,
            quantite: 1,
            prix_unitaire_ht: produit.prix_vente_ht,
            tva_taux: taxes.tva_active ? taxes.tva_taux : 0,
            css_taux: taxes.css_active ? taxes.css_taux : 0,
            remise: 0
        };
        setDevis({
            ...devis,
            lignes: [...devis.lignes, nouvelleLigne]
        });
        setShowProduitModal(false);
    };

    const supprimerLigne = (index) => {
        const nouvellesLignes = devis.lignes.filter((_, i) => i !== index);
        setDevis({ ...devis, lignes: nouvellesLignes });
    };

    const updateLigne = (index, champ, valeur) => {
        const nouvellesLignes = [...devis.lignes];
        nouvellesLignes[index][champ] = valeur;
        setDevis({ ...devis, lignes: nouvellesLignes });
    };

    // Calcul des totaux avec taxes dynamiques
    const calculerTotaux = () => {
        let total_ht = 0;
        let total_tva = 0;
        let total_css = 0;

        devis.lignes.forEach(ligne => {
            const montant_ligne_ht = ligne.quantite * ligne.prix_unitaire_ht * (1 - (ligne.remise || 0) / 100);
            total_ht += montant_ligne_ht;
            
            if (taxes.tva_active) {
                total_tva += montant_ligne_ht * (ligne.tva_taux || taxes.tva_taux) / 100;
            }
            if (taxes.css_active) {
                total_css += montant_ligne_ht * (ligne.css_taux || taxes.css_taux) / 100;
            }
        });

        const total_ttc = total_ht + total_tva + total_css;
        const net_a_payer = total_ttc - (devis.remise_globale || 0);

        return { total_ht, total_tva, total_css, total_ttc, net_a_payer };
    };

    const totaux = calculerTotaux();

    // Vérifier si le devis est modifiable
    const isEditable = () => {
        return devis.statut === 'brouillon';
    };

    // Vérifier si on peut changer le statut (uniquement de Envoyé vers Accepté/Refusé)
    const canChangeStatus = () => {
        return originalStatut === 'envoye' && ['accepte', 'refuse'].includes(devis.statut);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!devis.id_client) {
            setError("Veuillez sélectionner un client");
            return;
        }
        
        if (devis.lignes.length === 0) {
            setError("Veuillez ajouter au moins un produit");
            return;
        }

        // Validation spécifique selon le statut
        if (originalStatut === 'envoye' && !canChangeStatus()) {
            setError("Vous ne pouvez modifier que le statut (Accepté/Refusé) pour ce devis");
            return;
        }

        setSaving(true);
        setError("");

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/devis.php?id=${id}`,
                {
                    method: 'PUT',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(devis)
                }
            );

            const result = await response.json();

            if (result.success) {
                setSuccess("Devis mis à jour avec succès !");
                setTimeout(() => {
                    navigate('/ventes/devisListe');
                }, 2000);
            } else {
                setError(result.message || "Erreur lors de la mise à jour");
            }
        } catch (err) {
            setError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/devis.php?id=${id}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );

            const result = await response.json();

            if (result.success) {
                setShowDeleteModal(false);
                setSuccess("Devis supprimé avec succès !");
                setTimeout(() => {
                    navigate('/ventes/devisListe');
                }, 2000);
            } else {
                setError(result.message);
                setShowDeleteModal(false);
            }
        } catch (err) {
            setError("Erreur lors de la suppression");
            setShowDeleteModal(false);
        } finally {
            setDeleting(false);
        }
    };

    const getStatutBadge = (statut) => {
        const configs = {
            brouillon: { color: 'secondary', label: 'Brouillon', icon: 'ri-draft-line' },
            envoye: { color: 'primary', label: 'Envoyé', icon: 'ri-send-plane-line' },
            accepte: { color: 'success', label: 'Accepté', icon: 'ri-check-double-line' },
            refuse: { color: 'danger', label: 'Refusé', icon: 'ri-close-circle-line' },
            converti_facture: { color: 'info', label: 'Converti', icon: 'ri-file-list-3-line' }
        };
        const config = configs[statut] || configs.brouillon;
        return (
            <Badge 
                bg={config.color} 
                className="px-3 py-2"
                style={{
                    background: `linear-gradient(135deg, var(--bs-${config.color}), var(--bs-${config.color}-dark))`,
                    border: 'none',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
            >
                <i className={`${config.icon} me-1`}></i>
                {config.label}
            </Badge>
        );
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <Spinner animation="border" variant="primary" />
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="page-icon bg-primary-soft">
                                        <i className="ri-pencil-line text-primary"></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1">Modifier le devis</h1>
                                        <p className="page-subtitle mb-0">
                                            {devis.numero_proforma}
                                        </p>
                                    </div>
                                </div>
                                <Button 
                                    variant="outline-secondary" 
                                    onClick={() => navigate('/ventes/devisListe')}
                                    className="btn-gradient px-4 py-2"
                                >
                                    <i className="ri-arrow-left-line me-2"></i>
                                    Retour
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {error && (
                        <Alert variant="danger" onClose={() => setError("")} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2"></i>
                            {error}
                        </Alert>
                    )}
                    
                    {success && (
                        <Alert variant="success" className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2"></i>
                            {success}
                        </Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                        <Row>
                            {/* Informations générales */}
                            <Col lg={8}>
                                <Card className="border-0 shadow-sm mb-4">
                                    <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0">
                                            <i className="ri-information-line me-2"></i>
                                            Informations générales
                                        </h5>
                                        <div>
                                            {getStatutBadge(devis.statut)}
                                        </div>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Client <span className="text-danger">*</span></Form.Label>
                                                    <Form.Select
                                                        value={devis.id_client}
                                                        onChange={(e) => setDevis({...devis, id_client: e.target.value})}
                                                        disabled={!isEditable()}
                                                        required
                                                    >
                                                        <option value="">Sélectionner un client</option>
                                                        {clients.map(client => (
                                                            <option key={client.id_client} value={client.id_client}>
                                                                {client.raison_sociale} ({client.code_client})
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                    {!isEditable() && (
                                                        <Form.Text className="text-muted">
                                                            Le client ne peut pas être modifié pour ce devis
                                                        </Form.Text>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label>Date devis</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={devis.date_proforma}
                                                        onChange={(e) => setDevis({...devis, date_proforma: e.target.value})}
                                                        disabled={!isEditable()}
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label>Date validité</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={devis.date_validite}
                                                        onChange={(e) => setDevis({...devis, date_validite: e.target.value})}
                                                        disabled={!isEditable()}
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            
                                            {/* Sélecteur de statut - toujours visible mais avec options limitées selon le contexte */}
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label>Statut</Form.Label>
                                                    <Form.Select
                                                        value={devis.statut}
                                                        onChange={(e) => setDevis({...devis, statut: e.target.value})}
                                                        className="filter-select"
                                                    >
                                                        <option value="brouillon">📝 Brouillon</option>
                                                        <option value="envoye">📨 Envoyé</option>
                                                        <option value="accepte">✅ Accepté</option>
                                                        <option value="refuse">❌ Refusé</option>
                                                        <option value="converti_facture">📄 Converti en facture</option>
                                                    </Form.Select>
                                                    {originalStatut === 'envoye' && (
                                                        <Form.Text className="text-info">
                                                            Vous pouvez changer le statut en Accepté ou Refusé
                                                        </Form.Text>
                                                    )}
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label>Notes</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        value={devis.notes || ''}
                                                        onChange={(e) => setDevis({...devis, notes: e.target.value})}
                                                        disabled={!isEditable()}
                                                        placeholder="Conditions particulières, délais, etc..."
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                {/* Lignes de devis */}
                                <Card className="border-0 shadow-sm">
                                    <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0">
                                            <i className="ri-shopping-cart-line me-2"></i>
                                            Produits / Services
                                        </h5>
                                        <Button 
                                            variant="light" 
                                            size="sm"
                                            onClick={() => setShowProduitModal(true)}
                                            disabled={!isEditable()}
                                        >
                                            <i className="ri-add-line me-1"></i>
                                            Ajouter
                                        </Button>
                                    </Card.Header>
                                    <Card.Body>
                                        {devis.lignes.length > 0 ? (
                                            <div className="table-responsive">
                                                <Table className="align-middle">
                                                    <thead className="bg-light">
                                                        <tr>
                                                            <th>Référence</th>
                                                            <th>Désignation</th>
                                                            <th className="text-center">Qté</th>
                                                            <th className="text-end">Prix HT</th>
                                                            <th className="text-center">Remise %</th>
                                                            <th className="text-end">Total HT</th>
                                                            <th className="text-center">TVA</th>
                                                            <th className="text-center"></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {devis.lignes.map((ligne, index) => (
                                                            <tr key={index}>
                                                                <td>
                                                                    <Badge bg="light" text="dark" className="font-mono">
                                                                        {ligne.reference}
                                                                    </Badge>
                                                                </td>
                                                                <td className="fw-semibold">{ligne.designation}</td>
                                                                <td className="text-center" style={{ width: '80px' }}>
                                                                    <Form.Control
                                                                        type="number"
                                                                        min="1"
                                                                        value={ligne.quantite}
                                                                        onChange={(e) => updateLigne(index, 'quantite', parseInt(e.target.value) || 1)}
                                                                        size="sm"
                                                                        disabled={!isEditable()}
                                                                    />
                                                                </td>
                                                                <td className="text-end" style={{ width: '120px' }}>
                                                                    <Form.Control
                                                                        type="number"
                                                                        step="0.01"
                                                                        value={ligne.prix_unitaire_ht}
                                                                        onChange={(e) => updateLigne(index, 'prix_unitaire_ht', parseFloat(e.target.value) || 0)}
                                                                        size="sm"
                                                                        className="text-end"
                                                                        disabled={!isEditable()}
                                                                    />
                                                                </td>
                                                                <td className="text-center" style={{ width: '80px' }}>
                                                                    <Form.Control
                                                                        type="number"
                                                                        min="0"
                                                                        max="100"
                                                                        value={ligne.remise || 0}
                                                                        onChange={(e) => updateLigne(index, 'remise', parseFloat(e.target.value) || 0)}
                                                                        size="sm"
                                                                        disabled={!isEditable()}
                                                                    />
                                                                </td>
                                                                <td className="text-end fw-bold">
                                                                    {formatCurrency(ligne.quantite * ligne.prix_unitaire_ht * (1 - (ligne.remise || 0)/100))}
                                                                </td>
                                                                <td className="text-center text-warning">
                                                                    {taxes.tva_active ? (ligne.tva_taux || taxes.tva_taux) : 0}%
                                                                </td>
                                                                <td className="text-center">
                                                                    <Button 
                                                                        variant="link" 
                                                                        className="text-danger p-0"
                                                                        onClick={() => supprimerLigne(index)}
                                                                        disabled={!isEditable()}
                                                                    >
                                                                        <i className="ri-delete-bin-line"></i>
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        ) : (
                                            <div className="text-center py-5">
                                                <i className="ri-shopping-cart-line fs-48 text-muted mb-3"></i>
                                                <p className="text-muted">Aucun produit ajouté</p>
                                                <Button 
                                                    variant="outline-primary"
                                                    onClick={() => setShowProduitModal(true)}
                                                    disabled={!isEditable()}
                                                >
                                                    <i className="ri-add-line me-2"></i>
                                                    Ajouter un produit
                                                </Button>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Récapitulatif avec taxes dynamiques */}
                            <Col lg={4}>
                                <Card className="border-0 shadow-sm sticky-top" style={{ top: '20px' }}>
                                    <Card.Header className="bg-primary text-white">
                                        <h5 className="mb-0">
                                            <i className="ri-calculator-line me-2"></i>
                                            Récapitulatif
                                        </h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="recap-item d-flex justify-content-between mb-2">
                                            <span>Total HT</span>
                                            <span className="fw-bold">{formatCurrency(totaux.total_ht)}</span>
                                        </div>
                                        <div className="recap-item d-flex justify-content-between mb-2">
                                            <span>TVA {taxes.tva_active ? `(${taxes.tva_taux}%)` : '(inactive)'}</span>
                                            <span className="fw-bold text-warning">{formatCurrency(totaux.total_tva)}</span>
                                        </div>
                                        <div className="recap-item d-flex justify-content-between mb-2">
                                            <span>CSS {taxes.css_active ? `(${taxes.css_taux}%)` : '(inactive)'}</span>
                                            <span className="fw-bold text-info">{formatCurrency(totaux.total_css)}</span>
                                        </div>
                                        <hr />
                                        <div className="recap-item d-flex justify-content-between mb-3">
                                            <span className="fw-bold">Total TTC</span>
                                            <span className="fw-bold text-primary fs-5">{formatCurrency(totaux.total_ttc)}</span>
                                        </div>

                                        <Form.Group className="mb-3">
                                            <Form.Label>Remise globale (FCFA)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                value={devis.remise_globale}
                                                onChange={(e) => setDevis({...devis, remise_globale: parseFloat(e.target.value) || 0})}
                                                disabled={!isEditable()}
                                            />
                                        </Form.Group>

                                        <hr />
                                        <div className="recap-item d-flex justify-content-between mb-4">
                                            <span className="h5">Net à payer</span>
                                            <span className="h5 text-success">{formatCurrency(totaux.net_a_payer)}</span>
                                        </div>

                                        <div className="d-grid gap-2">
                                            <Button 
                                                type="submit" 
                                                variant="primary" 
                                                size="lg"
                                                disabled={saving || (!isEditable() && !canChangeStatus())}
                                            >
                                                {saving ? (
                                                    <>
                                                        <Spinner size="sm" className="me-2" />
                                                        Mise à jour...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="ri-save-line me-2"></i>
                                                        {originalStatut === 'envoye' ? 'Mettre à jour le statut' : 'Mettre à jour'}
                                                    </>
                                                )}
                                            </Button>
                                            
                                            {isEditable() && (
                                                <Button 
                                                    variant="danger" 
                                                    size="lg"
                                                    onClick={() => setShowDeleteModal(true)}
                                                >
                                                    <i className="ri-delete-bin-line me-2"></i>
                                                    Supprimer
                                                </Button>
                                            )}
                                            
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => navigate('/ventes/devisListe')}
                                            >
                                                Annuler
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Form>
                </div>
                <Footer />
            </div>

            {/* Modal de sélection des produits */}
            <Modal show={showProduitModal} onHide={() => setShowProduitModal(false)} size="lg" centered className="modern-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="ri-shopping-cart-line me-2 text-primary"></i>
                        Sélectionner un produit
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="table-responsive">
                        <Table hover className="align-middle modern-table">
                            <thead>
                                <tr>
                                    <th>Référence</th>
                                    <th>Désignation</th>
                                    <th className="text-end">Prix HT</th>
                                    <th className="text-center">Stock</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {produits.map(produit => (
                                    <tr key={produit.id_produit} className="product-row">
                                        <td>
                                            <Badge bg="light" text="dark" className="font-mono">
                                                {produit.reference}
                                            </Badge>
                                        </td>
                                        <td className="fw-semibold">{produit.designation}</td>
                                        <td className="text-end">{formatCurrency(produit.prix_vente_ht)}</td>
                                        <td className="text-center">
                                            <span className={produit.stock_actuel < produit.seuil_alerte ? 'text-danger fw-bold' : ''}>
                                                {produit.stock_actuel || 0}
                                            </span>
                                        </td>
                                        <td>
                                            <Button 
                                                size="sm" 
                                                variant="primary"
                                                onClick={() => ajouterLigne(produit)}
                                                className="btn-gradient"
                                            >
                                                <i className="ri-add-line"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Modal de confirmation de suppression */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="modern-modal">
                <Modal.Header closeButton>
                    <Modal.Title className="text-danger">
                        <i className="ri-alert-line me-2"></i>
                        Confirmer la suppression
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Êtes-vous sûr de vouloir supprimer ce devis ?</p>
                    <p className="fw-bold">{devis.numero_proforma} - {devis.client_nom}</p>
                    <p className="text-muted small">Cette action est irréversible.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Annuler
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                        {deleting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Suppression...
                            </>
                        ) : (
                            'Confirmer la suppression'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}